import { Box, IconButton, Paper } from "@mui/material";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import PatientSearchInput from "Pages/Patient/PatientDemographic/PatientSearchInput";
import React, { useRef } from "react";
import { FaMinus, FaPlus } from "react-icons/fa";
import { Loader } from "Shared/Common/Loader";
import useIsMobile from "Shared/hooks/useIsMobile";
import useLang from "Shared/hooks/useLanguage";
import NoRecord from "../../../Shared/Common/NoRecord";
import { ArrowDown, ArrowUp } from "../../../Shared/Icons";
import { useMicrobiologyResultDataContext } from "./ResultDataContext";
import Row from "./Row";
import {
  getActiveTabSortOrder,
  getVisibleTabHeaders,
  NormalizedData,
} from "./rowDataUtils";

interface ReqDataGridProps {
  tabsInfo?: any[];
  labTimeZone?: string | null;
  value: number;
  filters: any[];
  setFilters: (filters: any[]) => void;
  setCurPage: (page: number) => void;
}

const ReqDataGrid: React.FC<ReqDataGridProps> = ({
  tabsInfo,
  labTimeZone,
  value,
  filters,
  setFilters,
  setCurPage,
}) => {
  const {
    data,
    loading,
    filterData,
    checkedAll,
    selectedBox,
    rowsToExpand,
    loadGridData,
    setCheckedAll,
    setFilterData,
    setSelectedBox,
    setRowsToExpand,
    setIsMasterExpandTriggered,
  } = useMicrobiologyResultDataContext();

  const { t } = useLang();
  const isMobile = useIsMobile();
  const searchRef = useRef<any>(null);
  const activeSortOrder = getActiveTabSortOrder(data.gridHeaders, value);
  const visibleTabHeaders = getVisibleTabHeaders(tabsInfo);
  const isGridReady = !loading && visibleTabHeaders.length > 0;

  const handleSort = async (tabsDetail: any) => {
    if (!filterData) return;
    const currentDirection = searchRef.current.id;
    const newDirection = currentDirection === "asc" ? "desc" : "asc";
    searchRef.current.id = newDirection;
    filterData.sortColumn = tabsDetail?.columnKey;
    filterData.sortDirection = newDirection;
    await loadGridData(false);
  };

  const showExpandedBtn = rowsToExpand?.length;

  const handleOpen = () => {
    if (data?.gridData?.length > 0) {
      const idsToExpand = data.gridData
        .slice(0, 3)
        .map((item: any) => NormalizedData(item, "requisitionorderid"))
        .filter((id: any) => id !== null && id !== undefined);

      if (idsToExpand.length > 0) {
        setIsMasterExpandTriggered(true);
        setRowsToExpand(idsToExpand);
      }
    }
  };

  const handleClose = () => {
    setRowsToExpand([]);
    setIsMasterExpandTriggered(false);
  };

  return (
    <div>
      <Box sx={{ height: "auto", width: "100%" }}>
        <div className="table_bordered overflow-hidden">
          <TableContainer
            component={Paper}
            className="shadow-none"
            sx={
              isMobile
                ? {}
                : {
                  maxHeight: "calc(100vh - 100px)",
                  "&::-webkit-scrollbar": {
                    width: 7,
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#fff",
                  },
                  "&:hover": {
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "var(--kt-gray-400)",
                      borderRadius: 2,
                    },
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "var(--kt-gray-400)",
                    borderRadius: 2,
                  },
                }
            }
          >
            <Table
              aria-label="table with sticky header"
              stickyHeader
              className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
            >
              {isGridReady && (
                <TableHead>
                  <TableRow className="h-40px">
                    <TableCell></TableCell>
                    {activeSortOrder !== 1 ? (
                      <TableCell></TableCell>
                    ) : null}
                    {visibleTabHeaders.map((tabsDetail: any) => {
                      if (
                        !tabsDetail?.isShowOnUi ||
                        tabsDetail?.isExpandData ||
                        !tabsDetail?.isShow
                      ) {
                        return null;
                      }
                      return (
                        <TableCell key={tabsDetail.columnKey} sx={{ width: "max-content" }}>
                          <div className="d-flex justify-content-center align-items-center">
                            <div style={{ width: "max-content" }}>
                              {tabsDetail?.filterColumnsType && (
                                <PatientSearchInput
                                  sendRealColumnName
                                  column={tabsDetail}
                                  loadData={loadGridData}
                                  searchValue={filterData}
                                  setSearchValue={setFilterData}
                                  setFilters={setFilters}
                                  filters={filters}
                                  setCurPage={setCurPage}
                                />
                              )}
                            </div>
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  <TableRow>
                    {activeSortOrder !== 1 ? (
                      <TableCell>
                        <IconButton
                          aria-label="master expand"
                          size="small"
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: ".475rem",
                            backgroundColor: showExpandedBtn
                              ? "#8B0000"
                              : "#69A54B",
                            "&:hover": {
                              backgroundColor: showExpandedBtn
                                ? "#8B0000"
                                : "#5f9643",
                            },
                          }}
                        >
                          {showExpandedBtn ? (
                            <FaMinus color="#f5f8fa" onClick={handleClose} />
                          ) : (
                            <FaPlus color="#f5f8fa" onClick={handleOpen} />
                          )}
                        </IconButton>
                      </TableCell>
                    ) : null}
                    <TableCell sx={{ width: "max-content" }}>
                      <div className="d-flex justify-content-center">
                        {t("Actions")}
                      </div>
                    </TableCell>
                    {visibleTabHeaders.map((tabsDetail: any) => {
                      if (
                        !tabsDetail.isShowOnUi ||
                        tabsDetail.isExpandData ||
                        !tabsDetail.isShow
                      ) {
                        return null;
                      }

                      const nonSortableColumns = [
                        "ResultFile",
                        "View",
                        "Report",
                        "PrintLabel",
                      ];
                      const isSortable = !nonSortableColumns.includes(tabsDetail.columnKey);
                      const isActiveSort =
                        filterData?.sortColumn === tabsDetail.columnKey;

                      return (
                        <TableCell key={tabsDetail.columnKey} sx={{ width: "max-content" }}>
                          <div
                            onClick={() => {
                              if (isSortable) {
                                handleSort(tabsDetail);
                              }
                            }}
                            className={`d-flex justify-content-between ${isSortable ? "cursor-pointer" : ""
                              }`}
                            ref={isSortable ? searchRef : undefined}
                          >
                            <div style={{ width: "max-content" }}>
                              {t(tabsDetail?.columnLabel)}
                            </div>
                            {isSortable && (
                              <div className="d-flex justify-content-center align-items-center mx-4 mr-0">
                                <ArrowUp
                                  CustomeClass={`${filterData?.sortDirection === "asc" && isActiveSort
                                      ? "text-danger fs-6"
                                      : "text-gray-700 fs-7"
                                    } p-0 m-0`}
                                />
                                <ArrowDown
                                  CustomeClass={`${filterData?.sortDirection === "desc" && isActiveSort
                                      ? "text-danger fs-6"
                                      : "text-gray-700 fs-7"
                                    } p-0 m-0`}
                                />
                              </div>
                            )}
                          </div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {!isGridReady ? (
                  <TableCell colSpan={10}>
                    <Loader />
                  </TableCell>
                ) : data.gridData?.length ? (
                  data.gridData?.map((rowData: any) => (
                    <Row
                      key={NormalizedData(rowData, "requisitionorderid")}
                      RowData={rowData}
                      tabsInfo={tabsInfo}
                      labTimeZone={labTimeZone}
                    />
                  ))
                ) : (
                  <NoRecord />
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Box>
    </div>
  );
};

export default React.memo(ReqDataGrid);
